import { defineStore } from "pinia";
import type { ApplicationInterface } from "@/application/ApplicationInterface";
import { applicationApi } from "@/application/ApplicationApi";
import { ApplicationError } from "@/application/ApplicationError";
import type { LanguageInterface } from "@/application/LanguageInterface";
import type { EducationInterface } from "@/application/EducationInterface";
import { applicationLanguageApi } from "@/application/ApplicationLanguageApi";
import { applicationEducationApi } from "@/application/ApplicationEducationApi";

/**
 * app#85: in-memory state of the Personal info step that must survive the
 * "Back to previous step" round trip (the view is unmounted on the way).
 * Keyed by application id so a draft never leaks into another application.
 * Educations are a relation the partial save does not store, so a new
 * application would otherwise lose them until the first full submit.
 */
export interface PersonalInfoDraftInterface {
    applicationId: number | null;
    validatedSteps: number[];
    educations: EducationInterface[] | null;
}

function emptyPersonalInfoDraft(): PersonalInfoDraftInterface {
    return { applicationId: null, validatedSteps: [], educations: null };
}

export const useApplicationStore = defineStore("application", {
    state: () => {
        return {
            application: {} as ApplicationInterface,
            applicationEducations: [] as EducationInterface[],
            applicationLanguages: [
                {
                    language: "English",
                } as LanguageInterface,
            ] as LanguageInterface[],
            isLoaded: false,
            justSubmitted: false,
            // app#72: set by "Start New Application"; newApplicationGuard
            // consumes it once and skips the redirect to the open application.
            startNewRequested: false,
            personalInfoDraft: emptyPersonalInfoDraft(),
        };
    },
    actions: {
        logout(): void {
            this.setApplication({} as ApplicationInterface);
            this.isLoaded = false;
            this.startNewRequested = false;
            this.clearPersonalInfoDraft();
        },
        startNew(): void {
            this.setApplication({} as ApplicationInterface);
            this.isLoaded = true;
            this.startNewRequested = true;
            this.clearPersonalInfoDraft();
        },
        personalInfoDraftFor(
            appId: number | null | undefined
        ): PersonalInfoDraftInterface | null {
            if (!appId || this.personalInfoDraft.applicationId !== appId) {
                return null;
            }
            return this.personalInfoDraft;
        },
        rememberValidatedStep(appId: number, step: number): void {
            const draft = this.ownPersonalInfoDraft(appId);
            if (!draft.validatedSteps.includes(step)) {
                draft.validatedSteps.push(step);
            }
        },
        rememberDraftEducations(
            appId: number,
            educations: EducationInterface[]
        ): void {
            this.ownPersonalInfoDraft(appId).educations = educations;
        },
        clearPersonalInfoDraft(): void {
            this.personalInfoDraft = emptyPersonalInfoDraft();
        },
        // Returns the draft for appId, dropping whatever belonged to another
        // application first. Deliberately not tied to setApplication(): that
        // runs on the way back from the program step as well.
        ownPersonalInfoDraft(appId: number): PersonalInfoDraftInterface {
            if (this.personalInfoDraft.applicationId !== appId) {
                this.personalInfoDraft = {
                    ...emptyPersonalInfoDraft(),
                    applicationId: appId,
                };
            }
            return this.personalInfoDraft;
        },
        forgetApplication(appId: number): void {
            // app#79: the application was deleted; guards and components
            // reading the store must not keep working with it.
            if (this.application?.id !== appId) {
                return;
            }
            this.setApplication({} as ApplicationInterface);
            this.isLoaded = false;
            this.clearPersonalInfoDraft();
        },
        consumeStartNewRequest(): boolean {
            const requested = this.startNewRequested;
            this.startNewRequested = false;
            return requested;
        },
        setJustSubmitted(): void {
            this.justSubmitted = true;
        },
        setApplication(app: ApplicationInterface): void {
            this.application = app;
            this.justSubmitted = false;
            if (!this.application) {
                this.application = {} as ApplicationInterface;
            }

            if (this.application.educations) {
                this.applicationEducations = this.application.educations;
            } else {
                this.applicationEducations = [] as EducationInterface[];
            }
            if (this.application.languages) {
                this.applicationLanguages = this.application.languages;
                let hasEnglish = false;
                for (const language of this.applicationLanguages) {
                    if (language.language === "English") {
                        hasEnglish = true;
                    }
                }
                if (!hasEnglish) {
                    this.applicationLanguages.unshift({
                        language: "English",
                    } as LanguageInterface);
                }
            } else {
                this.applicationLanguages = [
                    {
                        language: "English",
                    } as LanguageInterface,
                ] as LanguageInterface[];
            }
        },
        async fetchOpen(): Promise<ApplicationInterface | null> {
            // app#72: always ask the API. The store may hold an application
            // opened via "Edit" which the backend no longer considers open.
            const app = await applicationApi.findOpen();
            if (!app) {
                this.setApplication({} as ApplicationInterface);
                this.isLoaded = true;
                return null;
            }
            this.setApplication(app);
            this.isLoaded = true;
            return this.application;
        },
        async saveProgram(): Promise<ApplicationInterface> {
            if (!this.application) {
                throw new ApplicationError("There is no application to save");
            }
            this.application = await applicationApi.saveProgramSelection(
                this.application
            );

            return this.application;
        },
        async ensureApplication(appId: number): Promise<ApplicationInterface> {
            const app = await applicationApi.findById(appId);
            if (
                this.isLoaded &&
                this.application &&
                this.application.id === appId &&
                this.application.version === app?.version
            ) {
                return this.application;
            }

            this.isLoaded = false;
            if (!app) {
                throw new ApplicationError(
                    "Requested application doesn't exist"
                );
            }
            this.setApplication(app);
            this.isLoaded = true;
            return this.application;
        },
        async savePersonalInfo(
            application: ApplicationInterface,
            languages: LanguageInterface[],
            educations: EducationInterface[]
        ) {
            const response: ApplicationInterface =
                await applicationApi.savePersonal(application);
            const keepLanguages = [] as number[];
            const newLanguages = [] as LanguageInterface[];
            let counter = 0;
            for (const language of languages) {
                if (
                    language.language === "English" &&
                    application.native_language === "English"
                ) {
                    // do not save english if it's already as native language
                    continue;
                }
                language.application_id = response.id;
                language.order = counter++;
                if (language.id) {
                    newLanguages.push(
                        await applicationLanguageApi.updateLanguage(language)
                    );
                    keepLanguages.push(language.id);
                } else {
                    newLanguages.push(
                        await applicationLanguageApi.createLanguage(language)
                    );
                }
            }
            for (const oldLang of response.languages) {
                if (keepLanguages.indexOf(oldLang.id as number) < 0) {
                    await applicationLanguageApi.deleteLanguage(
                        oldLang.id as number,
                        oldLang.application_id as number
                    );
                }
            }
            this.applicationLanguages = newLanguages;

            const keepEducations = [] as number[];
            const newEducations = [] as EducationInterface[];
            counter = 0;
            for (const education of educations) {
                education.application_id = response.id;
                education.order = counter++;
                if (education.id) {
                    newEducations.push(
                        await applicationEducationApi.updateEducation(education)
                    );
                    keepEducations.push(education.id);
                } else {
                    newEducations.push(
                        await applicationEducationApi.createEducation(education)
                    );
                }
            }
            for (const oldEducation of response.educations) {
                if (keepEducations.indexOf(oldEducation.id as number) < 0) {
                    await applicationEducationApi.deleteEducation(
                        oldEducation.id as number,
                        oldEducation.application_id as number
                    );
                }
            }
            this.applicationEducations = newEducations;
            // Submitted: the server now holds everything the draft protected.
            this.clearPersonalInfoDraft();
            await this.ensureApplication(application.id as number);
        },
        async updateVersionOnly(): Promise<void> {
            if (this.application) {
                const app = await applicationApi.findById(
                    this.application.id as number
                );
                if (app) {
                    this.application.version = app.version;
                }
            }
        },
        async markReviewed(): Promise<void> {
            if (!this.application.id || this.application.reviewed_at) {
                return;
            }
            await applicationApi.markReviewed(this.application);
            // The endpoint returns no body; keep the store in sync so the
            // stepper marks Review as passed without refetching.
            this.application.reviewed_at = new Date().toISOString();
        },
        async savePromoCode(application: ApplicationInterface): Promise<void> {
            await applicationApi.savePromoCode(application);
        },
        async saveIdDocumentType(
            type: "passport" | "nationalId" | null
        ): Promise<void> {
            const previous = this.application.student_id_document_type;
            this.application.student_id_document_type = type;
            try {
                const updated = await applicationApi.saveIdDocumentType(
                    this.application
                );
                this.application.version = updated.version;
            } catch (e) {
                this.application.student_id_document_type = previous;
                throw e;
            }
        },
    },
});
